import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Phone, Search, LogIn, Mail, Truck } from "lucide-react";
import { COMPANY, SOCIALS } from "@/data/turboData";
import { NAV } from "@/constants/testIds";
import { PlatformIcon, platformColor } from "./PlatformIcon";

const Logo = () => (
  <Link
    to="/"
    data-testid={NAV.logo}
    className="flex items-center gap-2.5 group"
    aria-label="Turbo Transit home"
  >
    <span className="grid place-items-center h-9 w-9 md:h-10 md:w-10 rounded-xl bg-[var(--tt-green)] text-white shadow-sm group-hover:scale-105 transition">
      <Truck className="h-5 w-5" strokeWidth={2.4} />
    </span>
    <span className="flex flex-col leading-tight">
      <span className="font-display font-black text-[var(--tt-navy)] text-lg md:text-xl tracking-tight">
        Turbo <span className="text-[var(--tt-green)]">Transit</span>
      </span>
      <span className="hidden lg:inline text-[9px] md:text-[10px] uppercase tracking-[0.18em] text-[var(--tt-zam-orange)] font-semibold whitespace-nowrap">
        Global logistics
      </span>
    </span>
  </Link>
);

const NavLinks = ({ onClick }) => {
  const cls = ({ isActive }) =>
    `relative text-sm font-medium transition-colors ${
      isActive
        ? "text-[var(--tt-navy)]"
        : "text-[var(--tt-ink-soft)] hover:text-[var(--tt-navy)]"
    }`;
  return (
    <>
      <NavLink to="/services" data-testid={NAV.servicesLink} className={cls} onClick={onClick}>
        Services
      </NavLink>
      <NavLink to="/freight/quote" data-testid="nav-freight-link" className={cls} onClick={onClick}>
        Freight
      </NavLink>
      <NavLink to="/fulfillment" className={cls} onClick={onClick}>
        Fulfillment
      </NavLink>
      <NavLink to="/shop" data-testid="nav-shop-link" className={cls} onClick={onClick}>
        Shop
      </NavLink>
      <NavLink to="/track" data-testid={NAV.trackLink} className={cls} onClick={onClick}>
        Track
      </NavLink>
      <NavLink to="/about" data-testid={NAV.aboutLink} className={cls} onClick={onClick}>
        About
      </NavLink>
      <NavLink to="/contact" data-testid={NAV.contactLink} className={cls} onClick={onClick}>
        Contact
      </NavLink>
    </>
  );
};

const TrackingMini = () => {
  const [val, setVal] = useState("");
  const nav = useNavigate();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        nav(`/track?id=${encodeURIComponent(val)}`);
      }}
      className="hidden lg:flex items-center gap-2 rounded-full border border-[var(--tt-line)] bg-white pl-3 pr-1 py-1"
    >
      <Search className="h-4 w-4 text-[var(--tt-muted)]" />
      <input
        data-testid={NAV.trackingInput}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="Track your shipment"
        className="w-44 bg-transparent text-sm outline-none placeholder:text-[var(--tt-muted)]"
      />
      <button
        data-testid={NAV.trackingSubmit}
        type="submit"
        className="rounded-full bg-[var(--tt-navy)] text-white text-xs font-semibold px-3 py-1.5 hover:bg-[var(--tt-navy-2)] transition"
      >
        Track
      </button>
    </form>
  );
};

const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      {/* Top utility bar */}
      <div className="hidden md:block bg-[var(--tt-navy)] text-white text-xs">
        <div className="mx-auto max-w-7xl px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-6 opacity-90">
            <a href={`tel:${COMPANY.phoneRaw}`} className="inline-flex items-center gap-1.5 hover:text-[var(--tt-accent-2)] transition">
              <Phone className="h-3.5 w-3.5" /> {COMPANY.phone}
            </a>
            <span className="opacity-70">{COMPANY.address.split(",").slice(0, 2).join(", ")}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="opacity-70">Follow:</span>
            {SOCIALS.map((s) => (
              <a
                key={s.key}
                href={s.url}
                data-testid={NAV.social(s.key)}
                aria-label={s.label}
                className="opacity-80 hover:opacity-100 hover:text-[var(--tt-accent-2)] transition"
              >
                <PlatformIcon platform={s.icon} className="h-3.5 w-3.5" />
              </a>
            ))}
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-40 bg-[var(--tt-bg)]/85 backdrop-blur-md border-b border-[var(--tt-line)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 md:h-[72px] flex items-center justify-between gap-4">
          <Logo />

          <nav className="hidden md:flex items-center gap-7">
            <NavLinks />
          </nav>

          <div className="flex items-center gap-3">
            <TrackingMini />
            <Link to="/portal/login" data-testid="nav-portal-signin" className="hidden md:inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--tt-navy)] hover:text-[var(--tt-green)] transition px-3 py-2.5 whitespace-nowrap">
              <LogIn className="h-4 w-4" /> Sign in
            </Link>
            <Link to="/quote" data-testid={NAV.quoteBtn} className="btn-primary !py-2.5 !px-4 text-sm whitespace-nowrap">
              Get a Quote
            </Link>
            <button
              data-testid={NAV.mobileToggle}
              onClick={() => setOpen(!open)}
              className="md:hidden p-2 -mr-2 text-[var(--tt-navy)]"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="md:hidden border-t border-[var(--tt-line)] bg-white">
            <div className="px-6 py-5 flex flex-col gap-4">
              <NavLinks onClick={() => setOpen(false)} />
              <Link to="/portal/login" onClick={() => setOpen(false)} data-testid="nav-portal-signin-mobile" className="flex items-center justify-center gap-2 rounded-xl border border-[var(--tt-green)] text-[var(--tt-green)] font-semibold py-2.5 hover:bg-[var(--tt-mint-soft)] transition">
                <LogIn className="h-4 w-4" /> Sign in to Customer Portal
              </Link>
              <div className="pt-3 border-t border-[var(--tt-line)] flex items-center justify-between">
                <a href={`tel:${COMPANY.phoneRaw}`} className="text-sm font-medium text-[var(--tt-navy)] inline-flex items-center gap-1.5">
                  <Phone className="h-4 w-4" /> Call us
                </a>
                <div className="flex items-center gap-3">
                  {SOCIALS.map((s) => (
                    <a key={s.key} href={s.url} aria-label={s.label} style={{ color: platformColor(s.icon) }}>
                      <PlatformIcon platform={s.icon} className="h-4 w-4" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
